import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { APP_BAR_HEIGHT, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiCog } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import {
  AppBar,
  Box,
  GridLegacy,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { HeaderLogo } from "./HeaderLogo/HeaderLogo";
import { SearchBar } from "./SearchBar/SearchBar";
import { UserMenu } from "./UserMenu/UserMenu";

function AdminSettings({ router }) {
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const { classes } = useStyles();

  if (!hasPermission(PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION)) {
    return null;
  }

  const handleClick = () => {
    router.navigate("/settings");
  };
  return (
    <MiIconButton
      onClick={handleClick}
      data-testid="settings-icon"
      className={classes.settingsIcon}
    >
      <MiIcon path={mdiCog} color={theme.palette.common.white} size={1.2} />
    </MiIconButton>
  );
}

/**
 * MiAppBar component
 * @param {Object} props
 * @param {string} props.tenantName - Name of the tenant to display in the app bar
 * @param {string} [props.appBarColor] - Optional background color for the app bar
 * @param {ReactNode} [props.appLogo] - Optional custom logo component to display in the app bar
 * @param {boolean} [props.hideSearchBar] - Flag to hide the search bar
 * @returns
 */
export function MiAppBar({
  tenantName = "",
  appBarColor,
  appLogo,
  hideSearchBar,
}) {
  const { classes } = useStyles({
    appBarColor,
  });
  const router = useRouter();
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  return (
    <AppBar
      position="relative"
      elevation={0}
      data-testid="mi-appbar"
      sx={{ minHeight: APP_BAR_HEIGHT }}
    >
      <Toolbar className={classes.toolBarContainer}>
        <GridLegacy
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <GridLegacy item xs={isTablet ? 8 : 2}>
            <Box className={classes.leftContainer}>
              {appLogo ?? <HeaderLogo />}
              <Box className={classes.divider} />
              <Typography className={classes.tenant}>{tenantName}</Typography>
            </Box>
          </GridLegacy>
          {!hideSearchBar && !isTablet && (
            <GridLegacy item xs={4} className={classes.searchBar}>
              <SearchBar />
            </GridLegacy>
          )}
          <GridLegacy item xs={1}>
            <Box className={classes.rightContainer}>
              <AdminSettings router={router} />
              <UserMenu />
            </Box>
          </GridLegacy>
          {!hideSearchBar && isTablet && (
            <GridLegacy container alignItems="center" justifyItems="center">
              <GridLegacy item xs={12} className={classes.searchBarContainer}>
                <SearchBar />
              </GridLegacy>
            </GridLegacy>
          )}
        </GridLegacy>
      </Toolbar>
    </AppBar>
  );
}

const useStyles = makeStyles()((theme, { appBarColor } = {}) => ({
  toolBarContainer: {
    background:
      appBarColor ??
      `conic-gradient(from 41.7deg at 100% 0%, ${theme.palette.cyan.main} 0deg, ${theme.palette.cyan.dark} 360deg)`,
    zIndex: theme.zIndex.appBar,
    flex: 1,
    [theme.breakpoints.up("sm")]: { minHeight: 60 },
  },
  leftContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    marginLeft: theme.spacing(6),
    minWidth: 418,
  },
  divider: {
    borderRight: `1px solid ${theme.palette.common.white}`,
    height: 20,
    opacity: 0.8,
  },
  tenant: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.common.white,
    whiteSpace: "nowrap",
    opacity: 0.8,
  },
  searchBar: {
    marginLeft: 128,
  },
  searchBarContainer: {
    padding: theme.spacing(2),
  },
  rightContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginRight: theme.spacing(6),
  },
  settingsIcon: {
    padding: theme.spacing(0, 3, 0, 0),
  },
}));
